<template>
    <Row type="none" class="bg-white rounded shadow min-h-screen">

        <div class="container pt-4">
            <b-button
                :variant="modelForm.isIncome ? 'primary' : 'outline-primary'"
                class="mr-2 w-36 focus:ring-0"
                @click="target(true)"
            >
                {{ $t('incomeAndOutcome.income') }}
            </b-button>
            <b-button
                :variant="modelForm.isIncome === false ? 'primary' : 'outline-primary'"
                class="mr-2 w-36 focus:ring-0"
                @click="target(false)"
            >
                {{ $t('incomeAndOutcome.outlay') }}
            </b-button>
            <b-button
                :variant="modelForm.isIncome === null ? 'primary' : 'outline-primary'"
                class="mr-2 w-36 focus:ring-0"
                @click="target(null)"
            >
                {{ $t('finance.transfer') }}
            </b-button>

        </div>

        <!--transfer-->
        <div v-if="modelForm.isIncome === null" class="grid grid-cols-12 gap-3 container mt-5">
            <div class="col-span-6 relative">
                <div class="font-bold text-sm bg-white px-1 text-fontColor z-30 absolute -top-5 ml-2">
                    {{ $t('finance.fromCount') }}<span class="text-red-500 text-xl">*</span>
                </div>
                <v-select
                    class="col-12 p-0"
                    :options="companyCounts"
                    label="name"
                    :reduce="options => options"
                    v-model="modelForm.companyCount"
                    @input="calcMaxSum"
                >
                    <template #no-options="{}">
                        {{ $t("selectError") }}
                    </template>
                </v-select>
                <InvalidForm
                    :state="modelNotEmptyState('companyCount')"
                    :text="errors['companyCount']"/>
            </div>

            <div class="col-span-6 relative">
                <div class="font-bold text-sm bg-white px-1 text-fontColor z-30 absolute -top-5 ml-2">
                    {{ $t('finance.whichCount') }}<span class="text-red-500 text-xl">*</span>
                </div>
                <v-select
                    class="col-12 p-0"
                    :options="companyCounts"
                    label="name"
                    :reduce="options => options"
                    v-model="modelForm.whichCount"
                >
                    <template #no-options="{}">
                        {{ $t("selectError") }}
                    </template>
                </v-select>
            </div>
        </div>

        <!-- income/Outcome-->
        <div v-else class="grid grid-cols-12 gap-3 container mt-5">
            <div class="col-span-6 relative">
                <div class="font-bold text-sm bg-white px-1 text-fontColor z-30 absolute -top-5 ml-2">
                    <span v-if="modelForm.isIncome">{{ $t('finance.whichCount') }}</span>
                    <span v-else>{{ $t('finance.fromCount') }}</span>
                    <span class="text-red-500 text-xl">*</span>
                </div>
                <v-select
                    class="px-0"
                    :options="companyCounts"
                    label="name"
                    :reduce="options => options"
                    v-model="modelForm.companyCount"
                    @input="calcMaxSum"
                >
                    <template #no-options="{}">
                        {{ $t("selectError") }}
                    </template>
                </v-select>
            </div>
            <div class="col-span-6 relative">
                <div class="font-bold text-sm bg-white px-1 text-fontColor z-30 absolute -top-5 ml-2">
                    {{ $t('category') }}<span class="text-red-500 text-xl">*</span>
                </div>
                <v-select
                    class="px-0"
                    :options="categories"
                    label="translatedName"
                    :reduce="options => options"
                    v-model="modelForm.category"
                >
                    <template #no-options="{}">
                        {{ $t("selectError") }}
                    </template>
                </v-select>
            </div>
        </div>
        <div v-if="isShowEmployees" class="grid grid-cols-12 gap-3 container mt-4">
            <div class="col-span-6 relative">
                <div class="font-bold text-sm bg-white px-1 text-fontColor z-30 absolute -top-5 ml-2">
                    {{ $t('employee.title') }}<span class="text-red-500 text-xl">*</span>
                </div>
                <v-select
                    class="col-12 p-0"
                    :options="getEmployees"
                    label="givenAndFamilyName"
                    :reduce="options => options.id"
                    v-model="modelForm.person"
                >
                    <template #no-options="{}">
                        {{ $t("selectError") }}
                    </template>
                </v-select>
            </div>
            <div class="col-span-6 relative" v-if="getEmployeeSalaries.length">
                <div class="font-bold text-sm bg-white px-1 text-fontColor z-30 absolute -top-5 ml-2">
                    {{ $t('kpi.salary.forWhatMonth') }}<span class="text-red-500 text-xl">*</span>
                </div>
                <b-form-input
                    class="border-gray-300 h-11 focus:ring-0"
                    id="date"
                    type="month"
                    v-model="getEmployeeSalaries[0].salaryOf"
                    @input="chooseSalaryOf()"
                />
            </div>
        </div>

        <div class="grid grid-cols-12 gap-3 container mt-4">
            <div class="col-span-12 md:col-span-6 relative">
                <div class="text-sm bg-white px-1 text-fontColor z-30 absolute -top-3 ml-2">
                    <span class="font-bold">{{ $t('sale.sum') }}</span>
                    <span v-if="!modelForm.isIncome" class="text-xs">{{ getMaxSum() }}</span>

                </div>
                <b-form-input
                    class="border-gray-300 h-11 focus:ring-0"
                    v-model="modelForm.sum"
                    min="0"
                    type="number"
                    :input="calcMaxSum"
                />
                <span class="text-xs text-red-500" v-if="isEnoughSum">
                    {{$t('filter.toSum') }}
                    {{
                        showCurrencyById(this.modelForm.companyCount.balanceCurrency.id, this.modelForm.companyCount.balance)
                    }}
                </span>
            </div>

            <div class="col-span-12 md:col-span-6 relative">
                <div class="font-bold text-sm bg-white px-1 text-fontColor z-30 absolute -top-3 ml-2">
                    {{ $t('currency') }}
                </div>
                <b-form-select
                    :disabled="isCurrencyChooseDisabled"
                    class="border-gray-300 h-11 focus:ring-0"
                    v-model="modelForm.sumCurrency"
                    :options="getCurrencies"
                    value-field="@id"
                    text-field="shortName"
                />
            </div>
        </div>

        <div class="grid grid-cols-12 gap-3 container mt-4">
            <div class="col-span-12 md:col-span-6 relative">
                <div class="text-sm bg-white px-1 text-fontColor z-30 absolute -top-3 ml-2">
                    {{ $t('date') }}
                </div>
                <InputBox
                    class="px-0"
                    type="date"
                    value="10-10-2013"
                    model-name="date"
                    check-to-model="modelNotEmptyState"
                    :errors="errors"
                    :model-form="modelForm"
                    :no-margin-top="true"
                />
            </div>

            <div class="col-span-12 md:col-span-6 relative">
                <div class="text-sm bg-white px-1 text-fontColor z-30 absolute -top-3 ml-2">
                    {{ $t('statistics.xaxis.hour') }}
                </div>
                <InputBox
                    class="px-0"
                    type="time"
                    model-name="time"
                    check-to-model="modelNotEmptyState"
                    :errors="errors"
                    :model-form="modelForm"
                    :no-margin-top="true"
                />
            </div>

            <div class="col-span-12 md:col-span-6 relative mt-3">
                <div class="text-sm bg-white px-1 text-fontColor z-30 absolute -top-3 ml-2">
                    {{ $t('productsInWarehouse.reason') }}
                </div>
                <b-form-textarea
                    class="border-gray-300 focus:ring-0"
                    v-model="modelForm.reason"
                />
            </div>
        </div>

        <div class="container flex mt-4 pb-4">
            <SubmitRow
                class="w-full"
                :text="submitButtonName"
                :is-disabled="hasErrorsIn(this.errors) || knowDisabled() || isChanged()"
                :is-loading="isLoading"
                :do-action="addOrEdit"
            />

            <SubmitRow
                btn-variant="w-36 h-11 bg-denyColor border-0 font-bold hover:bg-hoverDenyColor"
                :is-loading="isLoading"
                text="sale.transaction.cancel"
                :do-action="cancel"
            />
        </div>
    </Row>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import validatorMixin from "../../mixin/validatorMixin";
import Row from "../../components/html/Row";
import SubmitRow from "../../components/SubmitRow";
import accessMixin from "../../mixin/accessMixin";
import currencyFormatMixin from "../../mixin/currencyFormatMixin";
import dateFormattingMixin from "@/mixin/dateFormattingMixin";
import currencyRateMixin from "@/mixin/currencyRateMixin";
import InputBox from "@/components/InputBox.vue";
import vSelect from "vue-select";
import '../../style/vue-select.css';
import InvalidForm from "@/components/html/InvalidForm.vue";

export default {
    name: "CustomerAdd",
    mixins: [validatorMixin, accessMixin, currencyFormatMixin, dateFormattingMixin, currencyRateMixin],
    components: {
        InvalidForm,
        vSelect,
        InputBox,
        SubmitRow,
        Row,
    },
    data() {
        return {
            isCurrencyChooseDisabled: false,
            isEnoughSum: false,
            isShowEmployees: false,
            submitButtonName: 'add',
            companyCounts: [],
            errors: {
                sum: null,
                companyCount: null
            },
            isLoading: false,
            breadcrumbItems: [
                {name: 'home', url: ''},
                {name: 'incomeAndOutcome.title', url: 'incomeAndOutcome'},
                {name: 'add'},
            ],
            categories: [],
            modelForm: {
                id: null,
                isIncome: true,
                companyCount: null,
                whichCount: null,
                person: null,
                category: null,
                date: '2023-02-02',
                time: "08:00:00",
                sum: 0,
                sumCurrency: 0,
                reason: null,
                salaryOf: null,
            },
        }
    },
    computed: {
        ...mapGetters([
            'getIncomeAndOutcome',
            'getUserCompanyIdAt',
            'getCompanyCounts',
            'getEmployees',
            'getIncomeAndOutcomeCategories',
            'getCurrencies',
            'getEmployeeSalaries'
        ]),
    },
    watch: {
        'modelForm.companyCount'() {
            this.modelForm.sumCurrency = this.modelForm.companyCount.balanceCurrency['@id']
            this.companyCounts = this.getCompanyCounts.slice()

            let i = this.companyCounts.indexOf(this.modelForm.companyCount)

            if (i >= 0) {
                this.companyCounts.splice(i, 1);
            }

        },
        'modelForm.sum'() {
            this.calcMaxSum()
        },
        'modelForm.category'() {
            if (this.modelForm.category !== null) {
                this.isShowEmployees = this.modelForm.category.name === 'salary';
            }
        },
        'modelForm.person'() {
            if(this.modelForm.person !== null) {
                this.getEmployeeSalary(this.modelForm.person)
            }
        },
        getEmployeeSalaries() {
            if(this.getEmployeeSalaries.length) {
                this.modelForm.salaryOf = this.getEmployeeSalaries[0].salaryOf + '-01'
            }
        }
    },
    methods: {
        ...mapActions([
            'fetchIncomeAndOutcome',
            'putIncomeAndOutcome',
            'pushIncomeAndOutcome',
            'fetchCompanyCountsAll',
            'fetchIncomeAndOutcomeCategoriesAll',
            'fetchEmployeesAll',
            'fetchEmployeeSalary',
            'setBreadcrumbItems'
        ]),
        chooseSalaryOf(){
            this.modelForm.salaryOf = this.getEmployeeSalaries[0].salaryOf + '-01'
        },
        getEmployeeSalary(id) {
            this.fetchEmployeeSalary(id)
                .then(() => {
                    this.modelForm.sumCurrency = this.getEmployeeSalaries[0].employeeInformation.currency['@id']
                    this.isCurrencyChooseDisabled = true
                })
        },
        isChanged() {
            if (this.$route.name === 'incomeAndOutcome/edit') {
                if(this.getIncomeAndOutcome.person !== null) {
                    if(this.modelForm.person !== this.getIncomeAndOutcome.person.id) {
                        return true
                    }
                }

                if (
                    this.modelForm.isIncome === this.getIncomeAndOutcome.isIncome &&
                    this.modelForm.sum === this.getIncomeAndOutcome.sum &&
                    this.modelForm.sumCurrency === this.getIncomeAndOutcome.sumCurrency['@id'] &&
                    this.modelForm.reason === this.getIncomeAndOutcome.reason &&
                    this.modelForm.companyCount === this.getIncomeAndOutcome.companyCount &&
                    this.modelForm.whichCount === this.getIncomeAndOutcome.whichCount &&
                    this.modelForm.category === this.getIncomeAndOutcome.category
                ) {
                    return true
                }
            }

            return false
        },
        knowDisabled() {
            if (this.modelForm.isIncome === null) {
                return this.modelForm.companyCount === null ||
                    this.modelForm.whichCount === null ||
                    this.isEnoughSum ||
                    this.modelForm.sum === null ||
                    this.modelForm.sum === ' ' ||
                    this.modelForm.sumCurrency === 0
            } else {
                return this.modelForm.companyCount === null ||
                    this.modelForm.category === null ||
                    this.isEnoughSum ||
                    this.modelForm.sum === null ||
                    this.modelForm.sum === ' ' ||
                    this.modelForm.sumCurrency === 0
            }
        },
        getMaxSum() {
            if (this.modelForm.companyCount === null) {
                return ''
            }

            return '(max: ' + this.showCurrencyById(this.modelForm.companyCount.balanceCurrency.id, this.modelForm.companyCount.balance) + ')'
        },
        calcMaxSum() {
            if (this.modelForm.companyCount !== null && !this.modelForm.isIncome) {
                this.isEnoughSum = parseInt(this.modelForm.sum) > parseInt(this.modelForm.companyCount.balance)
            }
        },
        target(stat) {
            this.cancel()
            this.isShowEmployees = false
            this.isCurrencyChooseDisabled = false
            this.modelForm.date = this.getTodayDate()
            this.modelForm.time = this.getNowTime()
            this.modelForm.isIncome = stat
            this.companyCounts = this.getCompanyCounts.slice()

            if (stat !== null) {
                this.sortCategories()
            }
        },
        sortCategories() {
            this.categories = []

            for (let category of this.getIncomeAndOutcomeCategories) {
                category.translatedName = this.getName(category.name)

                if (this.modelForm.isIncome) {
                    if (category.isIncome) {
                        this.categories.unshift(category)
                    }
                } else {
                    if (!category.isIncome) {
                        this.categories.unshift(category)
                    }
                }
            }
        },
        cancel() {
            this.isCurrencyChooseDisabled = false

            this.modelForm = {
                id: null,
                isIncome: true,
                companyCount: null,
                whichCount: null,
                person: null,
                category: null,
                date: '2023-02-02',
                time: "08:00:00",
                sum: 0,
                sumCurrency: 0,
                reason: null,
                salaryOf: null,
            }
        },
        createAt() {
            return new Date(this.modelForm.date + ' ' + this.modelForm.time)
        },
        getName(categoryName) {
            switch (categoryName) {
                case "sale":
                case "purchase":
                case "rent":
                case "salary":
                case "communal":
                case "debt":
                case "credit":
                case "paymentSystem":
                    return this.$t("finance.income." + categoryName)

                default:
                    return categoryName
            }
        },
        addOrEdit() {
            if (this.$route.name === 'incomeAndOutcome/add') {
                this.add()
            } else {
                this.edit()
            }
        },
        add() {
            this.isLoading = true

            this.pushIncomeAndOutcome({
                sum: this.modelForm.sum,
                sumCurrency: this.modelForm.sumCurrency,
                createdAt: this.createAt(),
                companyCount: this.modelForm.companyCount['@id'],
                whichCount: this.modelForm.whichCount !== null ? this.modelForm.whichCount['@id'] : null,
                person: this.modelForm.person === null ? null : '/api/people/' + this.modelForm.person,
                category: this.modelForm.category !== null ? this.modelForm.category['@id'] : null,
                isIncome: this.modelForm.isIncome,
                reason: this.modelForm.reason,
                salaryMonthAt: this.modelForm.person === null ? null : this.modelForm.salaryOf
            })
                .then(() => {
                    this.$i18nHelper.toUrl('incomeAndOutcome')
                })

            this.isLoading = false
        },
        edit() {
            this.isLoading = true
            this.putIncomeAndOutcome({
                sum: this.modelForm.sum,
                sumCurrency: this.modelForm.sumCurrency,
                createdAt: this.createAt(),
                companyCount: this.modelForm.companyCount['@id'],
                whichCount: this.modelForm.whichCount !== null ? this.modelForm.whichCount['@id'] : null,
                person: this.modelForm.person === null ? null : '/api/people/' + this.modelForm.person,
                category: this.modelForm.category !== null ? this.modelForm.category['@id'] : null,
                isIncome: this.modelForm.isIncome,
                reason: this.modelForm.reason,
                salaryMonthAt: this.modelForm.person === null ? null : (this.modelForm.salaryOf.length === 10 ? this.modelForm.salaryOf : this.modelForm.salaryOf + '-01'),
                id: this.getIncomeAndOutcome.id
            })
                .then(() => {
                    this.$i18nHelper.toUrl('incomeAndOutcome')
                })
            setTimeout(() => {
                this.isLoading = false
            }, 5000)
        },
        getTodayDate(date = null) {
            if (date !== null) {
                let nowDate = new Date(date)
                let yearNow = nowDate.getFullYear()
                let monthNow = nowDate.getMonth() + 1
                let dateNow = nowDate.getDate()

                return yearNow + '-' + this.addZero(monthNow) + '-' + this.addZero(dateNow)
            }

            let yearNow = new Date().getFullYear()
            let monthNow = new Date().getMonth() + 1
            let dateNow = new Date().getDate()

            return yearNow + '-' + this.addZero(monthNow) + '-' + this.addZero(dateNow)
        },
        getNowTime(time = null) {
            if (time !== null) {
                let nowTime = new Date(time)
                let hours = nowTime.getHours()
                let minutes = nowTime.getMinutes()
                let seconds = nowTime.getSeconds()

                return this.addZero(hours) + ':' + this.addZero(minutes) + ':' + this.addZero(seconds)
            }

            let hours = new Date().getHours()
            let minutes = new Date().getMinutes()
            let seconds = new Date().getSeconds()

            return this.addZero(hours) + ':' + this.addZero(minutes) + ':' + this.addZero(seconds)
        },
        addZero(val) {
            return val < 10 ? '0' + val : val;
        },
    },
    created() {
        this.setBreadcrumbItems(this.breadcrumbItems)
    },
    mounted() {
        this.fetchCompanyCountsAll()
            .then(() => {
                this.companyCounts = this.getCompanyCounts.slice()
                for (let companyCount of this.getCompanyCounts) {
                    if(companyCount.name === 'Main') {
                        companyCount.name = this.$t('main')
                    }
                }
            })

        this.fetchEmployeesAll()
            .then(() => {
                for (let employee of this.getEmployees) {
                    employee.givenAndFamilyName = employee.givenName + ' ' + employee.familyName;
                }
            })
        this.fetchIncomeAndOutcomeCategoriesAll()
            .then(() => {
                this.modelForm.date = this.getTodayDate()
                this.modelForm.time = this.getNowTime()

                this.createAt()
                this.sortCategories()
            })

        if (this.$route.name === 'incomeAndOutcome/edit') {
            this.$root.showOverlay = true
            this.submitButtonName = 'edit'

            this.fetchIncomeAndOutcome(this.$route.params.id)
                .then(() => {
                    if (this.getIncomeAndOutcome.category !== null) {
                        this.getIncomeAndOutcome.category.translatedName = this.getName(this.getIncomeAndOutcome.category.name)
                    }

                    if (this.getIncomeAndOutcome.person !== null) {
                        this.getIncomeAndOutcome.person.givenAndFamilyName =
                            this.getIncomeAndOutcome.person.givenName + ' ' + this.getIncomeAndOutcome.person.familyName;
                    }

                    this.modelForm.id = this.getIncomeAndOutcome.id
                    this.modelForm.isIncome = this.getIncomeAndOutcome.isIncome
                    this.modelForm.sum = this.getIncomeAndOutcome.sum
                    this.modelForm.sumCurrency = this.getIncomeAndOutcome.sumCurrency['@id']
                    this.modelForm.reason = this.getIncomeAndOutcome.reason
                    this.modelForm.companyCount = this.getIncomeAndOutcome.companyCount
                    this.modelForm.whichCount = this.getIncomeAndOutcome.whichCount
                    this.modelForm.person = this.getIncomeAndOutcome.person === null ? null : this.getIncomeAndOutcome.person.id
                    this.modelForm.category = this.getIncomeAndOutcome.category
                    this.modelForm.date = this.getTodayDate(this.getIncomeAndOutcome.createdAt)
                    this.modelForm.time = this.getNowTime(this.getIncomeAndOutcome.createdAt)
                    this.modelForm.salaryOf = this.getIncomeAndOutcome.person === null ? null : this.getIncomeAndOutcome.salaryMonthAt.slice(0, 7)
                    this.$root.showOverlay = false
                })
        }
    },
}
</script>